/**
 * Dashboards (/workforce-explorer): the Workforce Explorer and the Annual I&G
 * Review's talent dashboard as sections of one page, navigated from the sidebar
 * like the hubs (?tab=workforce | talent).
 *
 * Each section has its own access and the page shows only the sections the
 * person may see (HR, 9 Oct 2026: access tailored to each section). A section
 * someone may not see is never mounted, so its requests never fire; the server
 * guards every one of them in any case.
 */
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { hasAnyPermission, hasRole } from '@/auth/permissions'
import { JourneyNavPublisher } from '@/lib/journeySections'
import { WORKFORCE_PERMISSION_KEYS, WORKFORCE_ROLES } from '@/lib/workforcePermissions'
import { canSeeTalentDashboard } from '@/lib/talentDashboardPermissions'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import WorkforceExplorerPage from './WorkforceExplorerPage'
import TalentDashboard from './talent/TalentDashboard'

type SectionId = 'workforce' | 'talent'

const SECTION_LABEL: Record<SectionId, string> = { workforce: 'Workforce', talent: 'Talent review' }

export default function DashboardsPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const allowed: Record<SectionId, boolean> = {
    workforce: hasRole(user, WORKFORCE_ROLES) || hasAnyPermission(user, WORKFORCE_PERMISSION_KEYS),
    talent: canSeeTalentDashboard(user),
  }
  const visible = (['workforce', 'talent'] as SectionId[]).filter((id) => allowed[id])
  const requested = searchParams.get('tab') as SectionId | null
  // Someone with neither falls through to the Workforce Explorer's own refusal.
  const section: SectionId = requested && visible.includes(requested) ? requested : visible[0] ?? 'workforce'
  const hidden = (['workforce', 'talent'] as SectionId[]).filter((id) => !allowed[id])

  return (
    <>
      <JourneyNavPublisher journey="dashboards" hidden={hidden} />
      {visible.length > 1 ? (
        // Phone: the sidebar is a drawer, so the sections get a selector here.
        <div className="px-6 pt-4 md:hidden">
          <Select value={section} onValueChange={(v) => setSearchParams({ tab: v })}>
            <SelectTrigger className="h-10 w-full text-sm font-semibold" aria-label="Dashboard">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visible.map((id) => (
                <SelectItem key={id} value={id}>
                  {SECTION_LABEL[id]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      {section === 'talent' ? <TalentDashboard /> : <WorkforceExplorerPage />}
    </>
  )
}
